﻿import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import {
  MANAGER_APPLICATION_REVIEW_MISSING_ID_TEXT,
  MANAGER_APPLICATION_REVIEW_PATH,
  MANAGER_APPLICATION_REVIEW_SUCCESS_TEXT,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/constants.ts';
import type {
  TManagerApplicationCommentsByDocumentId,
  TManagerApplicationDecision,
  TManagerApplicationDecisionsByDocumentId,
  TManagerReviewDocumentsByKey,
  TManagerReviewFile,
  TManagerReviewFilesById,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/types.ts';
import {
  buildManagerApplicationReviewPayload,
  areAllManagerDocumentDecisionsSelected,
  getManagerDocumentCommentValue,
  getManagerApplicationCreatedAtLabel,
  getRejectedManagerDocumentsWithoutComments,
  mergeManagerDocumentsById,
  mergeManagerDocumentsByKey,
  normalizeManagerDocumentFiles,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/utils/reviewUtils.ts';
import { toggleManagerDocumentDecision } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/utils/utils.ts';
import {
  DEFAULT_DOCUMENTS_PAGE,
  DEFAULT_DOCUMENTS_ROWS_PER_PAGE,
} from '~/modules/covenants/constants.tsx';
import { StatusFilterValue } from '~/modules/covenants/types.ts';
import {
  getCovenantsRequestErrorMessage,
  getSavedManagerUsername,
  openOrDownloadBlob,
} from '~/modules/covenants/utils.ts';
import { getCommentPlainText } from '~/components/CommentCell/utils.ts';
import {
  getManagerViewedDocuments,
  markManagerDocumentAsViewed,
  type TManagerViewedDocumentsById,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/utils/managerViewedDocumentsStorage.ts';
import {
  selectSupervisorApplicationDocumentsResponse,
  selectCurrentSupervisorApplication,
  selectCurrentSupervisorApplicationError,
  selectCurrentSupervisorApplicationStatus,
  selectSupervisorApplicationDocuments,
  selectSupervisorApplicationDocumentsError,
  selectSupervisorApplicationDocumentsPageConfiguration,
  selectSupervisorApplicationDocumentsStatus,
} from '~/store/covenants/selectors.ts';
import { resetCurrentSupervisorApplication } from '~/store/covenants/slice.ts';
import { FetchStatus } from '~/store/covenants/types.ts';
import {
  acceptSupervisorApplication,
  downloadSupervisorApplicationDocument,
  fetchSupervisorApplication,
  fetchSupervisorApplicationDocuments,
  updateSupervisorApplicationDocumentFileComment,
  updateSupervisorApplicationDocumentsComment,
} from '~/store/covenants/thunks/supervisorApplicationThunks.ts';
import type { AppDispatch } from '~/store/store.ts';
import useGetUserRole from '~/hooks/useGetUserRole';

export const useManagerApplicationReview = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId } = useParams<{ applicationId: string }>();
  const managerApplicationsPath = `${MANAGER_APPLICATION_REVIEW_PATH}${location.search || ''}`;

  const { isManagerLogin } = useGetUserRole();
  const currentSupervisorApplication = useSelector(selectCurrentSupervisorApplication);
  const applicationStatus = useSelector(selectCurrentSupervisorApplicationStatus);
  const applicationError = useSelector(selectCurrentSupervisorApplicationError);
  const supervisorApplicationDocumentsResponse = useSelector(
    selectSupervisorApplicationDocumentsResponse
  );
  const storeDocuments = useSelector(selectSupervisorApplicationDocuments);
  const storeDocumentsPageConfiguration = useSelector(
    selectSupervisorApplicationDocumentsPageConfiguration
  );
  const documentsStatus = useSelector(selectSupervisorApplicationDocumentsStatus);
  const documentsError = useSelector(selectSupervisorApplicationDocumentsError);
  const application =
    currentSupervisorApplication?.id === applicationId ? currentSupervisorApplication : null;
  const isCurrentDocumentsResponse =
    supervisorApplicationDocumentsResponse?.applicationId === applicationId;
  const documents = isCurrentDocumentsResponse ? storeDocuments : [];
  const documentsPageConfiguration = isCurrentDocumentsResponse
    ? storeDocumentsPageConfiguration
    : {
        page: DEFAULT_DOCUMENTS_PAGE,
        size: DEFAULT_DOCUMENTS_ROWS_PER_PAGE,
        totalElements: 0,
      };

  const [documentsPage, setDocumentsPage] = useState(DEFAULT_DOCUMENTS_PAGE);
  const [documentsRowsPerPage, setDocumentsRowsPerPage] = useState(DEFAULT_DOCUMENTS_ROWS_PER_PAGE);
  const [decisions, setDecisions] = useState<TManagerApplicationDecisionsByDocumentId>({});
  const [comments, setComments] = useState<TManagerApplicationCommentsByDocumentId>({});
  const [documentsById, setDocumentsById] = useState<TManagerReviewFilesById>({});
  const [documentsByKey, setDocumentsByKey] = useState<TManagerReviewDocumentsByKey>({});
  const [viewedDocumentIds, setViewedDocumentIds] = useState<TManagerViewedDocumentsById>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRejectAllActive, setIsRejectAllActive] = useState(false);
  const [modalErrorText, setModalErrorText] = useState('');
  const [successText, setSuccessText] = useState('');
  const [isMissingCommentsModalOpen, setIsMissingCommentsModalOpen] = useState(false);
  const [sharedRejectComment, setSharedRejectComment] = useState('');
  const [sharedRejectCommentError, setSharedRejectCommentError] = useState('');
  const [updatingDecisionDocumentIds, setUpdatingDecisionDocumentIds] = useState<
    Record<string, boolean>
  >({});

  const createdAtLabel = getManagerApplicationCreatedAtLabel(application);
  const isReviewEditable = application?.status === StatusFilterValue.Sent;
  const isInitialLoading =
    (applicationStatus === FetchStatus.Loading || documentsStatus === FetchStatus.Loading) &&
    !application &&
    !documents.length &&
    documentsPageConfiguration.totalElements === 0;
  const isSubmitDisabled =
    isSubmitting ||
    (isReviewEditable &&
      (application?.version == null ||
        !areAllManagerDocumentDecisionsSelected(
          decisions,
          documentsById,
          documentsByKey,
          documentsPageConfiguration.totalElements
        )));

  useEffect(() => {
    dispatch(resetCurrentSupervisorApplication());
    setModalErrorText('');
    setSuccessText('');
    setDocumentsPage(DEFAULT_DOCUMENTS_PAGE);
    setDocumentsRowsPerPage(DEFAULT_DOCUMENTS_ROWS_PER_PAGE);
    setDecisions({});
    setComments({});
    setDocumentsById({});
    setDocumentsByKey({});
    setViewedDocumentIds(
      getManagerViewedDocuments({
        manager: getSavedManagerUsername(),
        applicationId,
      })
    );
    setIsRejectAllActive(false);
    setIsMissingCommentsModalOpen(false);
    setSharedRejectComment('');
    setSharedRejectCommentError('');
    setUpdatingDecisionDocumentIds({});
  }, [applicationId, dispatch]);

  useEffect(() => {
    if (!applicationId || !isManagerLogin) {
      return;
    }

    dispatch(fetchSupervisorApplication({ applicationId }));
  }, [applicationId, isManagerLogin, dispatch]);

  useEffect(() => {
    if (!applicationId || !isManagerLogin) {
      return;
    }

    dispatch(
      fetchSupervisorApplicationDocuments({
        applicationId,
        page: documentsPage,
        size: documentsRowsPerPage,
      })
    );
  }, [applicationId, isManagerLogin, dispatch, documentsPage, documentsRowsPerPage]);

  useEffect(() => {
    if (applicationError?.message) {
      setModalErrorText(applicationError.message);
    }
  }, [applicationError]);

  useEffect(() => {
    if (documentsError?.message) {
      setModalErrorText(documentsError.message);
    }
  }, [documentsError]);

  useEffect(() => {
    if (!documents.length) {
      return;
    }

    setDocumentsById((previousState) => mergeManagerDocumentsById(previousState, documents));
    setDocumentsByKey((previousState) => mergeManagerDocumentsByKey(previousState, documents));
  }, [documents]);

  useEffect(() => {
    if (!isReviewEditable || !isRejectAllActive || !documents.length) {
      return;
    }

    const reviewFiles = documents.flatMap(normalizeManagerDocumentFiles);

    setDecisions((previousState) =>
      reviewFiles.reduce<TManagerApplicationDecisionsByDocumentId>(
        (nextState, document) => {
          if (nextState[document.id] == null) {
            nextState[document.id] = 'reject';
          }

          return nextState;
        },
        { ...previousState }
      )
    );
  }, [documents, isRejectAllActive, isReviewEditable]);

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(documentsPageConfiguration.totalElements / documentsRowsPerPage) - 1
    );

    if (documentsPage > maxPage) {
      setDocumentsPage(maxPage);
    }
  }, [documentsPage, documentsPageConfiguration.totalElements, documentsRowsPerPage]);

  const saveDocumentComment = async (documentId: string, comment: string) => {
    if (!applicationId || !isReviewEditable) {
      return;
    }

    const savedFile = await dispatch(
      updateSupervisorApplicationDocumentFileComment({
        applicationId,
        fileId: documentId,
        comment,
      })
    ).unwrap();
    const savedComment = savedFile.comment ?? comment;

    setComments((previousState) => ({
      ...previousState,
      [documentId]: savedComment,
    }));
    setDocumentsById((previousState) => {
      const document = previousState[documentId];

      if (!document) {
        return previousState;
      }

      return {
        ...previousState,
        [documentId]: {
          ...document,
          comment: savedComment,
        },
      };
    });
  };

  const handleDecisionChange = async (
    documentId: string,
    nextDecision: Exclude<TManagerApplicationDecision, null>
  ) => {
    if (!isReviewEditable || updatingDecisionDocumentIds[documentId]) {
      return;
    }

    const nextDocumentDecision = toggleManagerDocumentDecision(
      decisions[documentId] ?? null,
      nextDecision
    );
    const comment = getManagerDocumentCommentValue(documentId, comments, documentsById);

    if (
      nextDocumentDecision === 'accept' &&
      getCommentPlainText(comment).trim().length > 0
    ) {
      setUpdatingDecisionDocumentIds((previousState) => ({
        ...previousState,
        [documentId]: true,
      }));

      try {
        await saveDocumentComment(documentId, '');
      } catch (error) {
        setModalErrorText(getCovenantsRequestErrorMessage(error));
        return;
      } finally {
        setUpdatingDecisionDocumentIds((previousState) => {
          const nextState = { ...previousState };
          delete nextState[documentId];

          return nextState;
        });
      }
    }

    setDecisions((previousState) => {
      return {
        ...previousState,
        [documentId]: nextDocumentDecision,
      };
    });

    if (nextDocumentDecision === 'accept') {
      setComments((previousState) => ({
        ...previousState,
        [documentId]: '',
      }));
    }
  };

  const handleCommentSave = async (documentId: string, comment: string) => {
    try {
      await saveDocumentComment(documentId, comment);
    } catch (error) {
      setModalErrorText(getCovenantsRequestErrorMessage(error));
      throw error;
    }
  };

  const handleRejectAllDocuments = () => {
    if (!isReviewEditable) {
      return;
    }

    setIsRejectAllActive(true);
    setDecisions((previousState) =>
      Object.values(documentsById).reduce<TManagerApplicationDecisionsByDocumentId>(
        (nextState, document) => ({
          ...nextState,
          [document.id]: 'reject',
        }),
        { ...previousState }
      )
    );
  };

  const handlePreviewDocument = async (applicationDocument: TManagerReviewFile) => {
    if (!applicationId || !applicationDocument.id) {
      return;
    }

    try {
      const result = await dispatch(
        downloadSupervisorApplicationDocument({
          applicationId,
          id: applicationDocument.id,
        })
      ).unwrap();

      openOrDownloadBlob({
        blob: result.data,
        fileName: applicationDocument.fileName || result.filename,
        fallbackFileName: applicationDocument.documentName || 'document',
        contentType: result.contentType,
      });

      setViewedDocumentIds(
        markManagerDocumentAsViewed({
          manager: getSavedManagerUsername(),
          applicationId,
          documentFileId: applicationDocument.id,
        })
      );
    } catch (error) {
      setModalErrorText(getCovenantsRequestErrorMessage(error));
    }
  };

  const submitReview = async (nextComments: TManagerApplicationCommentsByDocumentId) => {
    if (!applicationId || application?.version == null || isSubmitting || !isReviewEditable) {
      return;
    }

    const payload = buildManagerApplicationReviewPayload(decisions, nextComments, documentsById);
    const hasSelectedDocuments =
      !!payload.accept.documents.length || !!payload.deny.documents.length;

    if (!hasSelectedDocuments) {
      return;
    }

    setIsSubmitting(true);

    try {
      await dispatch(
        acceptSupervisorApplication({
          applicationId,
          version: application.version,
          payload,
        })
      ).unwrap();

      setIsMissingCommentsModalOpen(false);
      setSharedRejectComment('');
      setSharedRejectCommentError('');
      setSuccessText(MANAGER_APPLICATION_REVIEW_SUCCESS_TEXT);
    } catch (error) {
      setModalErrorText(getCovenantsRequestErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!applicationId || application?.version == null || isSubmitting || !isReviewEditable) {
      return;
    }

    const missingRejectedComments = getRejectedManagerDocumentsWithoutComments(
      decisions,
      comments,
      documentsById
    );

    if (missingRejectedComments.length) {
      setSharedRejectComment('');
      setSharedRejectCommentError('');
      setIsMissingCommentsModalOpen(true);
      return;
    }

    await submitReview(comments);
  };

  const handleSharedRejectCommentChange = (value: string) => {
    setSharedRejectComment(value);

    if (sharedRejectCommentError) {
      setSharedRejectCommentError('');
    }
  };

  const handleMissingCommentsModalClose = () => {
    if (isSubmitting) {
      return;
    }

    setIsMissingCommentsModalOpen(false);
    setSharedRejectComment('');
    setSharedRejectCommentError('');
  };

  const handleMissingCommentsConfirm = async () => {
    const normalizedComment = sharedRejectComment.trim();

    if (!normalizedComment) {
      setSharedRejectCommentError('Вкажіть загальний коментар.');
      return;
    }

    if (!applicationId || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      await dispatch(
        updateSupervisorApplicationDocumentsComment({
          applicationId,
          comment: normalizedComment,
        })
      ).unwrap();

      setComments((previousState) =>
        Object.keys(documentsById).reduce<TManagerApplicationCommentsByDocumentId>(
          (nextState, documentId) => ({
            ...nextState,
            [documentId]: normalizedComment,
          }),
          { ...previousState }
        )
      );
      setDocumentsById((previousState) =>
        Object.entries(previousState).reduce<TManagerReviewFilesById>(
          (nextState, [documentId, document]) => ({
            ...nextState,
            [documentId]: {
              ...document,
              comment: normalizedComment,
            },
          }),
          {}
        )
      );
      setIsMissingCommentsModalOpen(false);
      setSharedRejectComment('');
      setSharedRejectCommentError('');
    } catch (error) {
      setModalErrorText(getCovenantsRequestErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const missingRejectedCommentsCount = getRejectedManagerDocumentsWithoutComments(
    decisions,
    comments,
    documentsById
  ).length;

  return {
    application,
    applicationId,
    comments,
    createdAtLabel,
    isManager: isManagerLogin,
    documents,
    documentsPage,
    documentsPageConfiguration,
    documentsRowsPerPage,
    documentsStatus,
    isInitialLoading,
    isReviewEditable,
    isSubmitDisabled,
    isSubmitting,
    isMissingCommentsModalOpen,
    modalErrorText,
    missingRejectedCommentsCount,
    sharedRejectComment,
    sharedRejectCommentError,
    successText,
    decisions,
    updatingDecisionDocumentIds,
    viewedDocumentIds,
    handleCommentSave,
    handleDecisionChange,
    handleMissingCommentsConfirm,
    handleMissingCommentsModalClose,
    handlePreviewDocument,
    handleRejectAllDocuments,
    handleSharedRejectCommentChange,
    handleSubmit,
    onCloseErrorModal: () => setModalErrorText(''),
    onCloseSuccessModal: () => setSuccessText(''),
    onMissingApplicationClose: () => navigate(managerApplicationsPath),
    onRowsPerPageChange: (nextRowsPerPage: number) => {
      setDocumentsRowsPerPage(nextRowsPerPage);
      setDocumentsPage(DEFAULT_DOCUMENTS_PAGE);
    },
    onSuccessAction: () => navigate(managerApplicationsPath),
    setDocumentsPage,
    missingApplicationText: MANAGER_APPLICATION_REVIEW_MISSING_ID_TEXT,
    getDocumentCommentValue: (documentId: string) =>
      getManagerDocumentCommentValue(documentId, comments, documentsById),
  };
};

export default useManagerApplicationReview;
